// Excepciones contables. Contabilidad → Movimientos.
//
// Lo que esta pantalla existe para impedir: que un documento se quede fuera del
// libro sin que nadie se entere. Hasta ahora, cuando la contabilización de una
// factura fallaba, el error se degradaba a un warning de PostgreSQL que no llega
// al navegador — el documento se guardaba, el asiento no se creaba y el contador
// lo descubría semanas después, cuadrando el balance.
//
// Hay DOS formas de quedarse fuera y las dos se ven aquí, porque para quien
// cuadra el libro son la misma cosa:
//   · «Falló»        — se intentó y reventó. Se conoce el motivo.
//   · «Sin intentar» — el trigger nunca llegó a correr. Ni asiento ni error:
//                      invisible por partida doble.
//
// Se agrupa POR CAUSA a propósito: 579 documentos con el mismo problema son UN
// problema, no quinientos setenta y nueve.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { field, rpc } from './shared';

interface Grupo {
  origen: string; situacion: string; causa: string;
  documentos: number; mas_antiguo: string | null; max_intentos: number;
}
interface Doc {
  origen: string; origen_id: string; fecha_documento: string | null;
  error: string | null; intentos: number; situacion: string; documento: string | null;
  ultimo_intento: string | null;
}
interface Bandeja { resumen: Grupo[]; documentos: Doc[]; total: number }

const ORIGEN_LABEL: Record<string, string> = {
  tickets: 'Ventas', tickets_buy: 'Compras', retenciones: 'Retenciones',
  inventario: 'Inventario', pos_sesion: 'Cierres de caja',
};
const ORIGENES = [
  { v: '', l: 'Todos' },
  { v: 'tickets_buy', l: 'Compras' },
  { v: 'tickets', l: 'Ventas' },
  { v: 'retenciones', l: 'Retenciones' },
  { v: 'inventario', l: 'Inventario' },
];

const Situacion = ({ s }: { s: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
    s === 'fallo' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>
    {s === 'fallo' ? 'Falló' : 'Sin intentar'}
  </span>
);

const diasDesde = (f: string | null) => {
  if (!f) return null;
  const d = Math.floor((Date.now() - new Date(f + 'T00:00:00').getTime()) / 86400000);
  return d > 0 ? d : 0;
};

export function ExcepcionesContables() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [origen, setOrigen] = useState('');

  const { data, isFetching } = useQuery<Bandeja>({
    queryKey: ['cont-excepciones', origen],
    queryFn: () => rpc<Bandeja>('cont_excepciones', { _origen: origen || null }),
  });
  const grupos = data?.resumen ?? [];
  const docs = data?.documentos ?? [];
  const total = data?.total ?? 0;
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['cont-excepciones'] });

  const reintentarTodo = useMutation({
    mutationFn: () => rpc<any>('cont_excepciones_reintentar', { _origen: origen || null }),
    onSuccess: (r) => {
      if (r.fallidos > 0) {
        toast.warning(`${r.contabilizados} contabilizado(s), ${r.fallidos} siguen fallando`);
      } else {
        toast.success(`${r.contabilizados} documento(s) contabilizados`);
      }
      refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reintentarUno = useMutation({
    mutationFn: (d: Doc) => rpc<any>('cont_excepcion_reintentar', { _origen: d.origen, _id: d.origen_id }),
    onSuccess: (r) => {
      if (r.ok) { toast.success('Contabilizado'); } else { toast.error(r.error); }
      refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Excepciones Contables</h1>
        {total > 0 && (
          <Button isLoading={reintentarTodo.isPending}
            onClick={async () => { if (await dlg.confirmar(`¿Reintentar contabilizar ${total} documento(s)?`)) reintentarTodo.mutate(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Reintentar todo
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Documentos que no llegaron al libro. Unos fallaron al contabilizarse y se conoce el motivo; otros nunca
        se intentaron y no dejaron ni error. Resuelve la causa —normalmente una cuenta sin mapear o un período
        cerrado— y reintenta.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-muted-foreground">Origen
          <select className={`${field} w-48 mt-1`} value={origen} onChange={(e) => setOrigen(e.target.value)}>
            {ORIGENES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </label>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </div>

      {total === 0 && !isFetching && (
        <Card><CardContent className="py-12 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-3" />
          <p className="font-medium">No hay documentos fuera del libro.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Todo lo que debía contabilizarse tiene su asiento.
          </p>
        </CardContent></Card>
      )}

      {grupos.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Por causa ({grupos.length})
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Causa</th><th className="py-2 pr-3">Origen</th>
                <th className="py-2 pr-3">Situación</th><th className="py-2 pr-3 text-right">Documentos</th>
                <th className="py-2 pr-3">Más antiguo</th><th className="py-2 pr-3 text-right">Intentos</th>
              </tr></thead>
              <tbody>
                {grupos.map((g, i) => {
                  const d = diasDesde(g.mas_antiguo);
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3">{g.causa}</td>
                      <td className="py-2 pr-3">{ORIGEN_LABEL[g.origen] ?? g.origen}</td>
                      <td className="py-2 pr-3"><Situacion s={g.situacion} /></td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{g.documentos}</td>
                      <td className="py-2 pr-3">{g.mas_antiguo ?? '—'}
                        {d !== null && d > 30 && <div className="text-xs text-red-600">hace {d} días</div>}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{g.max_intentos || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {docs.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">
            Documentos ({docs.length}{total > docs.length ? ` de ${total}` : ''})
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Origen</th>
                <th className="py-2 pr-3">Documento</th><th className="py-2 pr-3">Situación</th>
                <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={d.origen + d.origen_id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_documento ?? '—'}</td>
                    <td className="py-2 pr-3">{ORIGEN_LABEL[d.origen] ?? d.origen}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{d.documento ?? '—'}</td>
                    <td className="py-2 pr-3"><Situacion s={d.situacion} /></td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground max-w-md">
                      {d.error ?? 'El proceso de contabilización nunca llegó a correr para este documento'}
                      {d.intentos > 1 && <span className="ml-1">({d.intentos} intentos)</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => reintentarUno.mutate(d)}
                        disabled={reintentarUno.isPending}
                        title="Reintentar este documento"
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50">
                        <RefreshCw className="w-3.5 h-3.5" /> Reintentar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
